import { HttpClient, HttpContext } from "@angular/common/http";
import { Observable } from "rxjs";
import {
  Letterhead,
  LetterheadLight
} from "src/app/backend/models/letterhead/letterhead";
import { environment } from "src/environments/environment";
import { map } from "rxjs/operators";
import { PageRequest } from "src/app/support/page-request";
import { PaginatedResult } from "src/app/support/paginated-result";
import { isUndefined, omitBy } from "lodash-es";
import { UserNotify } from "src/app/support/notify-decorator";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { LetterheadCreate } from "src/app/backend/models/letterhead/letterhead-create";
import { LetterheadUpdate } from "src/app/backend/models/letterhead/letterhead-update";
import { LetterheadBackend } from "src/app/backend/api/letterhead/letterhead.backend";
import { HTTP_CONTEXT_TOKEN_NO_SWAP_DEEP_PROPERTY_ID } from "src/app/core/http-context-tokens";

export class RocketLetterheadBackend extends LetterheadBackend {
  constructor(private http: HttpClient) {
    super();
  }

  load(id: string): Observable<Letterhead> {
    return this.http
      .get<Letterhead>(`${environment.snoots.api.diagnostic}/letterheads/${id}`)
      .pipe(map((letterhead) => extend(letterhead)));
  }

  loadLight(id: string): Observable<LetterheadLight> {
    return this.http.get<LetterheadLight>(
      `${environment.snoots.api.diagnostic}/letterheads/${id}`,
      {
        params: { light: true }
      }
    );
  }

  loadDefault(): Observable<Letterhead | undefined> {
    return this.listAll().pipe(
      map((letterheads) => letterheads.find((x) => x.default))
    );
  }

  loadDefaultLight(): Observable<LetterheadLight | undefined> {
    return this.listAllLight().pipe(
      map((letterheads) => letterheads.find((x) => x.default))
    );
  }

  list<T = Letterhead>(
    request: PageRequest
  ): Observable<PaginatedResult<Letterhead>> {
    return this.http
      .get<PaginatedResult<Letterhead>>(
        `${environment.snoots.api.diagnostic}/letterheads`,
        {
          params: omitBy(
            {
              p: request.page,
              s: request.pageSize,
              ...request.filterValues
            },
            isUndefined
          )
        }
      )
      .pipe(
        map((response) => ({
          ...response,
          docs: response.docs.map((letterhead) => extend(letterhead))
        }))
      );
  }

  listAll(): Observable<Letterhead[]> {
    return this.list({ page: 1, pageSize: 10000 }).pipe(
      map((response) => response.docs.map(extend))
    );
  }

  listAllLight(): Observable<LetterheadLight[]> {
    return this.list<LetterheadLight>({
      page: 1,
      pageSize: 10000,
      filterValues: { light: true }
    }).pipe(map((response) => response.docs));
  }

  @UserNotify({
    successMessage: marker("Carta intestata creata"),
    errorMessage: marker("Impossibile creare la carta intestata")
  })
  create(payload: LetterheadCreate): Observable<Letterhead> {
    return this.http.post<Letterhead>(
      `${environment.snoots.api.diagnostic}/letterheads`,
      payload
    );
  }

  @UserNotify({
    successMessage: marker("Carta intestata eliminata"),
    errorMessage: marker("Impossibile eliminare la carta intestata")
  })
  delete(id: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.snoots.api.diagnostic}/letterheads/${id}`
    );
  }

  @UserNotify({
    successMessage: marker("Carta intestata aggiornata"),
    errorMessage: marker("Impossibile aggiornare la carta intestata")
  })
  update(id: string, payload: LetterheadUpdate): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.diagnostic}/letterheads/${id}`,
      payload,
      {
        context: new HttpContext().set(
          HTTP_CONTEXT_TOKEN_NO_SWAP_DEEP_PROPERTY_ID,
          true
        )
      }
    );
  }
}

function extend(letterhead: Letterhead): Letterhead {
  return letterhead;
}
